import { CSSProperties, forwardRef, ReactElement, useEffect, useState } from "react";
import { classNames } from "../utils/classNames";
import { Transition } from "@headlessui/react";
import { FloatingOverlay } from "./FloatingOverlay";
import { useFloatingPopover } from "./floatingHelpers";
import styles from "./Tooltip.module.css";

function findTooltipTarget(el: EventTarget | null) {
  if (el === null || !(el instanceof Element)) {
    return null;
  } else if (el instanceof HTMLElement && el.dataset.tooltip) {
    return el;
  }
  return findTooltipTarget(el.parentElement);
}

export type TooltipProps = {
  open?: boolean;
  content?: ReactElement | string | number;
  style?: CSSProperties;
  arrowStyle?: CSSProperties;
  arrowPosition?: "top" | "bottom";
  delay?: boolean;
  color?: "default" | "error";
  setArrowEl?: (arrowEl: HTMLDivElement | null) => void;
};

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      open,
      content,
      style,
      arrowStyle,
      arrowPosition,
      setArrowEl,
      delay = true,
      color = "default",
    },
    ref,
  ) => {
    return (
      <Transition
        show={open}
        enter={classNames("tw-transition-opacity tw-duration-250", delay && "tw-delay-500")}
        enterFrom={"tw-opacity-0"}
        enterTo={"tw-opacity-100"}
        leaveTo={"tw-opacity-0"}
      >
        <div
          ref={ref}
          className={classNames(
            "tooltip",
            "tw-relative",
            "color-gray-xx-soft",
            color === "default" ? "bg-gray-x-hard" : color === "error" ? "bg-accent2" : undefined,
            "tw-rounded",
            "tw-shadow",
            "tw-p-2",
            "tw-pointer-events-none",
            "tw-w-max",
            !open && "tw-hidden",
          )}
          style={style}
        >
          <div className={"tw-max-w-prose"}>{content}</div>
          <div
            ref={setArrowEl}
            className={classNames(
              "tooltip-arrow",
              "tw-absolute",
              "tw-w-0",
              "tw-h-0",
              "tw-border-x-8",
              color === "default"
                ? styles.tooltipArrowDefault
                : color === "error"
                  ? styles.tooltipArrowError
                  : undefined,
              arrowPosition === "top" ? "tw-border-b-8" : "tw-border-t-8",
            )}
            style={{
              ...arrowStyle,
              top: arrowPosition === "top" ? "-8px" : undefined,
              bottom: arrowPosition === "bottom" ? "-8px" : undefined,
            }}
          />
        </div>
      </Transition>
    );
  },
);

export type FocusTooltipProps = {
  children: (props: { targetRef: (element: HTMLElement | null) => void }) => ReactElement;
  placement?: "top" | "bottom";
} & Pick<TooltipProps, "color" | "content">;

export function FocusTooltip({ content, children, ...props }: FocusTooltipProps) {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { floatingStyles, middlewareData, placement } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl,
    arrowEl,
    autoUpdate: true,
    placement: props.placement,
  });

  useEffect(() => {
    if (!targetEl) {
      return;
    }

    function handleFocus() {
      if (content) {
        setOpen(true);
      }
    }

    function handleBlur() {
      setOpen(false);
    }

    targetEl.addEventListener("focus", handleFocus, true);
    targetEl.addEventListener("blur", handleBlur, true);
    return () => {
      targetEl.removeEventListener("focus", handleFocus, true);
      targetEl.removeEventListener("blur", handleFocus, true);
    };
  }, [content, targetEl]);

  return (
    <>
      {children({ targetRef: setTargetEl })}
      <FloatingOverlay layer={"tooltip"}>
        <Tooltip
          ref={setTooltipEl}
          open={open}
          content={content}
          style={floatingStyles}
          arrowPosition={placement === "bottom" ? "top" : "bottom"}
          arrowStyle={{
            left: `${middlewareData.arrow?.x ?? 0}px`,
            top: `${middlewareData.arrow?.y ?? 0}px`,
          }}
          setArrowEl={setArrowEl}
          delay={false}
          {...props}
        />
      </FloatingOverlay>
    </>
  );
}

export type HoverTooltipProps = {
  children: (props: { targetRef: (element: HTMLElement | null) => void }) => ReactElement;
  placement?: "top" | "bottom";
} & Pick<TooltipProps, "color" | "content">;

export function HoverTooltip({ content, children, ...props }: HoverTooltipProps) {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { floatingStyles, middlewareData, placement } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl,
    arrowEl,
    autoUpdate: true,
    placement: props.placement,
  });

  useEffect(() => {
    if (!targetEl) {
      return;
    }

    function handleMouseEnter() {
      if (content) {
        setOpen(true);
      }
    }

    function handleMouseLeave() {
      setOpen(false);
    }

    targetEl.addEventListener("mouseenter", handleMouseEnter);
    targetEl.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      targetEl.removeEventListener("mouseenter", handleMouseEnter);
      targetEl.removeEventListener("mouseleave", handleMouseEnter);
    };
  }, [content, targetEl]);

  return (
    <>
      {children({ targetRef: setTargetEl })}
      <FloatingOverlay layer={"tooltip"}>
        <Tooltip
          ref={setTooltipEl}
          open={open}
          content={content}
          style={floatingStyles}
          arrowPosition={placement === "bottom" ? "top" : "bottom"}
          arrowStyle={{
            left: `${middlewareData.arrow?.x ?? 0}px`,
            top: `${middlewareData.arrow?.y ?? 0}px`,
          }}
          setArrowEl={setArrowEl}
          delay={false}
          {...props}
        />
      </FloatingOverlay>
    </>
  );
}

let isInitialized = false;
let touchTarget: HTMLElement | null = null;
let target: HTMLElement | null = null;

export function TooltipManager() {
  const [currentTarget, setCurrentTarget] = useState<HTMLElement | null>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const open = currentTarget !== null;
  const { floatingStyles, middlewareData, placement } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl: currentTarget,
    arrowEl,
    autoUpdate: true,
  });

  useEffect(() => {
    if (isInitialized) {
      return;
    }
    isInitialized = true;

    function mouseDownListener() {
      if (!isInitialized) {
        return;
      }
      if (target) {
        target = null;
        setCurrentTarget(null);
      }
    }

    function mouseOverListener(event: MouseEvent) {
      if (!isInitialized) {
        return;
      }
      target = findTooltipTarget(event.target);
      if (touchTarget && target === touchTarget) {
        // prevent tooltips when tapping on touch devices
        return;
      }
      if (target !== currentTarget) {
        setCurrentTarget(target);
      }
    }

    function touchEndListener(event: TouchEvent) {
      touchTarget = findTooltipTarget(event.target);
    }

    document.body.addEventListener("touchend", touchEndListener);
    document.body.addEventListener("mouseover", mouseOverListener);
    document.body.addEventListener("mousedown", mouseDownListener, true);
    return () => {
      document.body.removeEventListener("touchend", touchEndListener);
      document.body.removeEventListener("mouseover", mouseOverListener);
      document.body.removeEventListener("mousedown", mouseDownListener, true);
      isInitialized = false;
    };
  }, [currentTarget]);

  return (
    <FloatingOverlay layer={"tooltip"}>
      <Tooltip
        ref={setTooltipEl}
        open={open}
        content={currentTarget?.dataset["tooltip"]}
        style={floatingStyles}
        arrowPosition={placement === "bottom" ? "top" : "bottom"}
        arrowStyle={{
          left: `${middlewareData.arrow?.x ?? 0}px`,
          top: `${middlewareData.arrow?.y ?? 0}px`,
        }}
        setArrowEl={setArrowEl}
      />
    </FloatingOverlay>
  );
}
